import { NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { IconName } from '@sc/data-access-assets';
import { Glass, GlassQuestion, ImpactType } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import {
  CarMiniatureImgComponent,
  ErrorComponent,
  IconComponent,
  RadioButtonComponent,
} from '@sc/ui-components';
import { randomId } from '@sc/utils';

@Component({
  selector: 'sc-glass-choice-accordion',
  styleUrls: ['./glass-choice-accordion.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatExpansionModule,
    NgIf,
    IconComponent,
    MatTooltipModule,
    MatRadioModule,
    NgFor,
    RadioButtonComponent,
    CarMiniatureImgComponent,
    ErrorComponent,
    TranslateModule,
  ],
  template: `
    <mat-expansion-panel
      #expansionPanel
      (opened)="_isExpanded = true"
      (closed)="_isExpanded = false"
    >
      <!-- ############################
              Question
              #############################-->
      <mat-expansion-panel-header>
        <mat-panel-title>
          <!-- Expanded title-->
          <div *ngIf="_isExpanded">
            <p class="question-title sc-design-caption">
              {{
                'SinisterDeclarationAccordionPanel.questionTitle'
                  | translate: { numberQuestion: numberQuestion }
              }}
            </p>
            <p class="question-text sc-design-text-4-bold">
              {{ getQuestionExpandedText() | translate }}
              <sc-icon
                class="right-icon"
                *ngIf="glassQuestion.key == 'inVisionField'"
                [name]="'info'"
                [inline]="true"
                (mouseenter)="tooltip.show()"
                (click)="tooltip.toggle()"
                [matTooltip]="getInfoText(glassQuestion.key) | translate"
                matTooltipPosition="above"
                data-html="true"
                #tooltip="matTooltip"
              ></sc-icon>
            </p>
          </div>

          <!-- Not Expanded title-->
          <div *ngIf="!_isExpanded">
            <p class="closed-title sc-design-caption">
              <sc-icon
                *ngIf="isQuestionError"
                [name]="'warning_circle'"
                [inline]="true"
              ></sc-icon>
              {{ getQuestionClosedText() | translate }}
            </p>
            <p class="closed-text sc-design-text-4-bold">
              {{ getAnswerText(answer) | translate }}
            </p>
          </div>
        </mat-panel-title>
      </mat-expansion-panel-header>

      <!-- ############################
              Answers
              #############################-->
      <mat-radio-group>
        <sc-radio-button
          [customClassList]="['radio-button-block']"
          *ngFor="let answerItem of getPossibleAnswers()"
          [id]="getAnswerId(answerItem)"
          [value]="answerItem"
          [label]="getAnswerText(answerItem) | translate"
          [rightIcon]="getIconName(answerItem)"
          [toolTipText]="getDisclaimerText(answerItem) | translate"
          [selected]="answer === answerItem"
          (spaceOrEnterVsButtonClicked)="onAnswerChange(answerItem, $event)"
        >
          <sc-car-miniature-img
            additionalAction
            *ngIf="glassQuestion?.key === 'glass' && displayQuestionIcons"
            aria-hidden="true"
            [glassExtended]="
              glassQuestion?.key === 'glass' ? $any(answerItem) : null
            "
          ></sc-car-miniature-img>
        </sc-radio-button>
      </mat-radio-group>
      <sc-error
        id="errorId"
        [displayCondition]="isQuestionError"
        [error]="{
          title: 'MatErrors.requiredQuestionErrorTitle' | translate,
          message: 'MatErrors.requiredQuestionErrorMessage' | translate
        }"
      ></sc-error>
    </mat-expansion-panel>
  `,
})
export class GlassChoiceAccordion implements AfterViewInit {
  @ViewChild('expansionPanel') expansionPanel: MatExpansionPanel;
  @Input() numberQuestion: number;
  @Input() glass: Glass;
  @Input() answer: string | boolean;
  @Input() glassQuestion: GlassQuestion;
  @Input() set isExpanded(isExpanded) {
    this._isExpanded = isExpanded;
    this.shouldBeExpanded = isExpanded;
  }
  @Input() set reInitExpansion(changeValue) {
    this._isExpanded = this.shouldBeExpanded;

    if (this._isExpanded) {
      this.expansionPanel?.open();
    } else {
      this.expansionPanel?.close();
    }
  }
  @Input() displayQuestionIcons = false;
  @Input() isQuestionError: boolean;
  @Output() answerChangeEvent = new EventEmitter<{
    answer: string | boolean;
    isDisplayUpdated: boolean;
  }>();

  errorId;

  _isExpanded: boolean;
  shouldBeExpanded: boolean;

  constructor(public translatorService: TranslatorService) {
    this.errorId = randomId(8);
  }

  ngAfterViewInit(): void {
    if (this._isExpanded) {
      this.expansionPanel?.open();
    } else {
      this.expansionPanel?.close();
    }
  }

  getQuestionAddress(): string {
    return `SinisterDeclarationAccordionPanel.${
      this.numberQuestion === 1 ? '' : this.glass + '.'
    }${this.glassQuestion.key}`;
  }

  getQuestionExpandedText(): string {
    return `${this.getQuestionAddress()}.questionExpandedText`;
  }

  getQuestionClosedText(): string {
    return `${this.getQuestionAddress()}.questionClosedText`;
  }

  getAnswerText(answerItem: string | boolean) {
    return answerItem !== null && answerItem !== undefined
      ? `${this.getQuestionAddress()}.answers.${answerItem}`
      : '';
  }

  getInfoText(answerItem: string | boolean) {
    return (
      'SinisterDeclarationAccordionPanel.' + this.glass + '.info.' + answerItem
    );
  }

  getDisclaimerText(answerItem: string | boolean) {
    return this.numberQuestion === 1
      ? `${this.getQuestionAddress()}.disclaimer.${answerItem}`
      : '';
  }

  getAnswerId(answerItem: string | boolean) {
    return `${this.glassQuestion.key}-${answerItem}`;
  }

  getPossibleAnswers(): (string | boolean)[] {
    return this.glassQuestion.values.map((value) =>
      typeof value === 'object' ? value.value : value
    );
  }

  onAnswerChange(answer, spaceOrEnter) {
    this.answerChangeEvent.emit({
      answer: answer,
      isDisplayUpdated: spaceOrEnter,
    });
  }

  getIconName(answerItem) {
    let hasDisclaimerText = false;

    const translation = this.getDisclaimerText(answerItem);
    if (translation?.length > 0) {
      hasDisclaimerText =
        this.translatorService.getInstantTranslation(translation)?.length;
    }
    return hasDisclaimerText
      ? 'info'
      : this.displayQuestionIcons && this.glassQuestion?.key === 'impactsType'
      ? this.getImpactTypeIconName(answerItem)
      : null;
  }

  getImpactTypeIconName(impactsType: ImpactType): IconName {
    if (impactsType === '1impact' || impactsType === '3impactsLess')
      return '2impactsLess';
    else if (impactsType === 'more3impactsOrCrack') return 'crack';
    else return impactsType;
  }
}
